import { Component, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogRef} from '@angular/material/dialog';
import {FpaAdditionalData} from '../../fpa/fpa-additional-data';
import {FpaSubmissionData} from '../../fpa/fpa-submission-data';
import {SelectionItem} from '../../selections/selection-item';
import {FpaService} from '../../services/fpa-service';
import {SelectionService} from '../../services/selection-service';
import {DateUtils} from '../../utils/date-utils';
import {StringUtils} from '../../utils/string-utils';
import {MatButton} from '@angular/material/button';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import { NgClass } from '@angular/common';
import {FormsModule} from '@angular/forms';
import {EntityService} from "../../services/entity-service";
import {EntityUtils} from "../../utils/entity-utils";
import {Entity} from "../../entity/entity";

@Component({
    selector: 'fpa-submission-dialog',
    templateUrl: './fpa-submission-dialog.component.html',
    styleUrls: ['./fpa-submission-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatProgressSpinner, NgClass, MatDatepickerInput, MatDatepickerToggle, MatDatepicker, MatDialogActions, MatButton]
})
export class FpaSubmissionDialogComponent implements OnInit {
  private readonly fpaService = inject(FpaService);
  private readonly selectionService = inject(SelectionService);
  private readonly entityService = inject(EntityService);
  readonly dialogRef = inject<MatDialogRef<FpaSubmissionDialogComponent>>(MatDialogRef);
  private readonly data = inject(MAT_DIALOG_DATA);


  private readonly entityId: string;
  private readonly maxFirstShipmentDays: number = 365;
  private entityRevision: number;
  public readonly submissionData = signal<FpaSubmissionData>(undefined!);
  public readonly isBusy = signal<boolean>(undefined!);
  public readonly errorMessage = signal<string>(undefined!);
  public readonly fpaCompetitors = signal<SelectionItem[]>(undefined!);
  private entityType: string;


  constructor
  () {
    this.entityId = this.data.entityId;
    this.entityRevision = this.data.entityRevision;
    this.entityType = EntityUtils.determineEntityType(this.entityId);
  }

  ngOnInit(): void {
    this.isBusy.set(true);

    this.entityService.getEntity(this.entityType, this.entityId, this.entityRevision).subscribe({
      next: (entity:Entity) => {
        this.selectionService.getFpaCompetitors(entity.brandID).subscribe(result => this.fpaCompetitors.set(result));
      },
      error: error => this.handleError(error),
    });

    this.fpaService.getAdditionalData(this.entityId, this.entityRevision).subscribe({
      next: (result: FpaSubmissionData) => {
        this.submissionData.set(result);
        if (!this.submissionData().additionalData.specifyingEngineer) {
          let additionalData: FpaAdditionalData = new FpaAdditionalData();
          additionalData.entityId = this.entityId;
          additionalData.targetPrice = 0;
          additionalData.competitorId = 33;
          const today = new Date();
          additionalData.projectBidDate = today;
          additionalData.iecListedEqual = false;
          additionalData.basisOfDesign = 33;
          const oneMonthFromNow: Date = new Date();
          oneMonthFromNow.setDate(today.getDate() + 30);
          additionalData.expectedFirstShipment = oneMonthFromNow.toISOString().substring(0, 10);
          this.submissionData().additionalData = additionalData;
        }
        if (this.submissionData().additionalData.basisOfDesign <= 0) {
          this.submissionData().additionalData.basisOfDesign = 33;
          const oneMonthFromNow: Date = new Date();
          oneMonthFromNow.setDate(oneMonthFromNow.getDate() + 30);
          this.submissionData().additionalData.expectedFirstShipment = oneMonthFromNow.toISOString().substring(0, 10);
        }
        this.isBusy.set(false);
      }, error: (error) => this.handleError(error)
    });
  }

  public formatDisplayDate(input: Date): Date {
    return DateUtils.formatDisplay(input);
  }

  public setProjectBidDate(newValue: Date) {
    this.submissionData().additionalData.projectBidDate = DateUtils.formatOutput(newValue);
  }

  public saveAndClose(): void {
    this.errorMessage.set(undefined!);
    if (this.isValidExpectedFirstShipment()) {
      this.isBusy.set(true);
      this.fpaService.submitScenarios(this.submissionData()).subscribe({
        next: () => this.dialogRef.close(true),
        error: (error) => this.handleError(error)
      });
    } else {
      this.errorMessage.set('Please enter a valid date (MM/dd/yyyy or yyyy-MM-dd) greater than today and less than ' + this.maxFirstShipmentDays + ' days from now for the expected first shipment');
    }
  }

  private isValidExpectedFirstShipment(): boolean {
    if (StringUtils.isBlank(this.submissionData()?.additionalData?.expectedFirstShipment)) {
      return false;
    }
    const input: Date = DateUtils.parseDate(this.submissionData().additionalData.expectedFirstShipment);
    if (!input) {
      return false;
    }
    const today: Date = new Date();
    const maxDate: Date = new Date();
    maxDate.setDate(maxDate.getDate() + this.maxFirstShipmentDays);
    return input.getTime() >= today.getTime() && input.getTime() <= maxDate.getTime();
  }

  private handleError(error: any): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }

  public cancel(): void {
    this.dialogRef.close(false);
  }

}
